import * as React from 'react';
import { Table, Toolbar, ToolbarItem, Button, ButtonGroup, Icon } from '../../common';
import PortOpeningRuleOnPrem from '../../../models/entities/PortOpeningRuleOnPrem';
import { RulesFormProps } from './porRule/OnPremAbstractRulesForm';
import Paginator from '../../common/tables/Paginator';
import { PORColumn, RuleColumnsLayout } from './RuleColumnsLayout';
import POROperationBadge from '../../../components/PortOpeningRequests/components/POROperationBadge';
import PORTypeBadge from '../../../components/PortOpeningRequests/components/PORTypeBadge';
import TrashcanModal from '../components/TrashcanModal';
import { RuleOperation } from '../../../models/enums/POR/RuleOperation';
import './OnPremRulesTable.css';
import { WorkflowState } from '../../../models/enums/WorkflowState';
import Select, { StylesConfig } from "react-select";
import BulkUploadModal from './porRule/BulkUploadModal';
import ImplementedRulesModal from './ImplementedRulesModal';
import { OnPremPortOpeningRequestsTypes } from '../../../models/enums/POR/OnPremPortOpeningRequestsTypes';
import RuleModalInternetDMZ from './porRule/RuleModalInternetDMZ';
import RuleModalCertifiedConnection from './porRule/RuleModalCertifiedConnection';
import { PortOpeningRequestsDirection } from '../../../models/enums/POR/PortOpeningRequestsDirection';
import OnPremRiskPorts from '../../../models/entities/OnPremRiskPorts';
import CSVGeneratorButton from '../../common/CSVGeneratorButton';

interface RulesTableProps {
	rules: PortOpeningRuleOnPrem[];
	porType: OnPremPortOpeningRequestsTypes;
	readonly?: boolean;
	onChange: (rules: PortOpeningRuleOnPrem[]) => any;
	implementedRules?: PortOpeningRuleOnPrem[];
	selectedFromExisting?: string[];
	setSelectedFromExisting?: (rule: string[]) => any;
	loadingImplementedRules?: boolean;
	failedImplementedRules?: boolean;
	fromDetails?: boolean;
	currentWorkflowState?: number;
	direction?: PortOpeningRequestsDirection;
	risksPorts: OnPremRiskPorts;
	hasRiskPorts?: (value: boolean) => any;
	onRiskTypeChange?: (types: { hasCritical: boolean; hasHigh: boolean }) => void;
}

interface RulesTableState {
	columns: PORColumn[];
	ruleModalVisible: boolean;
	editingRule?: PortOpeningRuleOnPrem;
	bulkUploadModalVisible: boolean;
	pageRows: PortOpeningRuleOnPrem[];
	implementedRuleModalVisible: boolean;
	optionValueSelected: string,
	showTrashcanModal: boolean;
	riskPorts: string[];
}

export class OnPremRulesTable extends React.Component<RulesTableProps, RulesTableState> {

	constructor(props: RulesTableProps | Readonly<RulesTableProps>) {
		super(props);
		this.state = {
			columns: [],
			ruleModalVisible: false,
			bulkUploadModalVisible: false,
			pageRows: [],
			implementedRuleModalVisible: false,
			showTrashcanModal: false,
			optionValueSelected: "",
			riskPorts: [],
		};
	}

	componentDidMount() {
		this.setColumns();
		this.validateRisksPorts();
	}

	componentDidUpdate(prevProps: RulesTableProps) {
		if (prevProps.porType?.value !== this.props.porType?.value) {
			this.setColumns();
		}
		if (this.props.rules !== prevProps.rules)
			this.validateRisksPorts();
	}

	setColumns() {
		this.setState({
			columns: RuleColumnsLayout.get(this.props.porType)
		});
	}

	parseMultipleValues = (values: string) => {
		return values?.split(/\n/).join(';')
			.split(' ').join('');
	};

	validateRange(criticalPortsSet: Set<number>, highPortsSet: Set<number>, destinationPorts: string): string {
		const [startRange, endRange] = destinationPorts.split('-');
		const isInRange = (port: number) => port >= parseInt(startRange) && port <= parseInt(endRange);
		for (const port of criticalPortsSet){
			if (isInRange(port))
				return 'criticalRiskPort';
		}
		for (const port of highPortsSet){
			if (isInRange(port))
				return 'highRiskPort';
		}
		return ''
	}

	validateSinglePort(criticalPortsSet: Set<number>, highPortsSet: Set<number>, destinationPorts: string): string {
		const ports = this.parseMultipleValues(destinationPorts).split(';');
		for (const port of ports) {
			if (criticalPortsSet.has(parseInt(port)))
				return 'criticalRiskPort';
			if (highPortsSet.has(parseInt(port)))
				return 'highRiskPort';
		}
		return ''
	}

	validateRisksPorts() {
		const riskPorts = this.props.rules.map((rule) => {
			if (rule.protocol.toLowerCase().includes('icmp'))
				return 'highRiskPort';
			const criticalPortsSet = new Set<number>(this.props.risksPorts?.criticals[rule.protocol.toLowerCase()]);
			const highPortsSet = new Set<number>(this.props.risksPorts?.high[rule.protocol.toLowerCase()]);
			if (rule.destinationPorts.includes('-')) {
				return this.validateRange(criticalPortsSet, highPortsSet, rule.destinationPorts);
			}
			else {
				return this.validateSinglePort(criticalPortsSet, highPortsSet, rule.destinationPorts);
			}
		});
		const hasCritical = riskPorts.some(r => r === 'criticalRiskPort');
		const hasHigh = riskPorts.some(r => r === 'highRiskPort');
		(hasCritical || hasHigh) ? this.props.hasRiskPorts(true) : this.props.hasRiskPorts(false);
		this.props.onRiskTypeChange?.({ hasCritical, hasHigh });
		this.setState({ riskPorts });
	}

	getRulesModalProps(): RulesFormProps {
		let editingRule = null;
		if (this.state.editingRule != null) {
			editingRule = { ...this.state.editingRule };
			editingRule.name = editingRule.name
		}
		return {
			visible: this.state.ruleModalVisible,
			onConfirm: this.handleRuleChange,
			onClose: () => this.setState({ editingRule: null, ruleModalVisible: false }),
			editingRule: editingRule,
		};
	}

	handleRuleChange = (rule: PortOpeningRuleOnPrem) => {
		let { rules } = this.props;
		if (this.state.editingRule) {
			const index = rules.indexOf(this.state.editingRule);
			rules = [...rules];
			rules[index] = rule;
		} else {
			rules = [...rules, rule];
		}
		this.props.onChange(rules);
		this.setState({ ruleModalVisible: false, editingRule: null });
	};

	handleDeleteAll = () => {
		this.props.onChange([]);
		this.resetTrashcanModal();
	};

	handleDeleteOrRemoveRule = (ruleToRemove: PortOpeningRuleOnPrem) => {
		this.setState({ showTrashcanModal: true, editingRule: ruleToRemove })
	};

	handleRemoveRule = () => {
		const rules = this.props.rules.filter(rule => rule !== this.state.editingRule);
		this.props.setSelectedFromExisting(this.props.selectedFromExisting.filter(ruleName => ruleName !== this.state.editingRule.name));
		this.props.onChange(rules);
		this.resetTrashcanModal();
	};

	handleDeleteRule = () => {
		let editingRule = { ...this.state.editingRule };
		editingRule.name = editingRule.name;
		editingRule.azureName = undefined;
		editingRule.operation = RuleOperation.Remove;
		this.handleRuleChange(editingRule);
		this.resetTrashcanModal();
	};

	resetTrashcanModal = () => {
		this.setState({
			editingRule: null,
			showTrashcanModal: false
		})
	}

	closeTrashcanModal = this.resetTrashcanModal;

	handleBulkUpload = (rules: PortOpeningRuleOnPrem[]) => {
		this.setState({
			bulkUploadModalVisible: false,
			optionValueSelected: '',
		});
		this.props.onChange([...this.props.rules, ...rules]);
	};

	handlePageChange = (page: PortOpeningRuleOnPrem[]) => {
		this.setState({
			pageRows: page
		});
	};

	handleImplementedRulesOnSuccess = (impRules: PortOpeningRuleOnPrem[]) => {
		this.setState({
			implementedRuleModalVisible: false,
			optionValueSelected: '',
		});
		this.props.setSelectedFromExisting([...this.props.selectedFromExisting, ...impRules.map(rule => rule.name)])
		this.props.onChange([...this.props.rules, ...impRules]);
	};

	typeIs(type: OnPremPortOpeningRequestsTypes) {
		return this.props.porType === type;
	}

	getOperationName(rule: PortOpeningRuleOnPrem) {
		if (this.props.fromDetails && this.props.currentWorkflowState !== WorkflowState.Review.value && this.props.currentWorkflowState !== WorkflowState.Draft.value) {
			return this.state.columns.find(column => column.header === 'Operation').cell(rule)
		}
		const operationColumn = this.state.columns.find(column => column.header === 'Operation')
		return operationColumn.cell(rule)

	}

	handleOptionSelected = (option) => {
		this.setState({ optionValueSelected: option.value });
		if (option.value === "import-new-rules") {
			if (!option.isDisabled) this.setState({ bulkUploadModalVisible: true });
		} else {
			if (!option.isDisabled) this.setState({ implementedRuleModalVisible: true });
		}
	}

	CustomOption = (props: { data: any; innerRef: any; innerProps: any; isDisabled: boolean }) => {
		const { data, innerRef, innerProps, isDisabled } = props;
		return (
			<div
				style={{ cursor: `${isDisabled ? 'not-allowed' : 'pointer'}` }}
				ref={innerRef}
				{...innerProps}
				className='custom-option-box'
			>
				<div style={{ color: `${isDisabled ? '#999' : '#1E55B1'}`, fontSize: 'large', }}>{data.isLoading ? data.loadingMessage : data.label}</div>
				<div style={{ color: '#999', fontSize: 'small', wordWrap: 'break-word', width: '180px' }}>{data.subtext}</div>
			</div>
		)
	}

	styles: StylesConfig<any, false> = {
		control: (css) => ({ ...css, width: 200, zIndex: 5 }),
	};

	render() {
		const tableHeaders = this.state.columns?.filter(column => column.header !== "Protocol" && column.header !== "Description").map((column) => (
			<Table.Head.Cell key={column.header}>
				{column.header}
			</Table.Head.Cell>
		));
		const tableRows = [];
		const defaultLimit = 10;
		const options = [
			{
				value: "import-new-rules",
				label: 'Import new rules',
				subtext: 'Add new rules at the same time with CSV file',
				isDisabled: this.props.porType == null, //|| !(this.props.validVirtualNetworks.length > 0),
				isLoading: this.props.porType == null, //|| !(this.props.validVirtualNetworks.length > 0),
				loadingMessage: this.props.porType != null ? 'Loading Virtual Networks...' : 'Please select a POR Direction'
			},
			{
				value: "import-existing-rule",
				label: 'Import existing rules',
				subtext: 'Add existing rules to delete or modify',
				isDisabled: this.props.loadingImplementedRules || !(this.props.implementedRules?.length > 0),
				isLoading: this.props.loadingImplementedRules || !(this.props.implementedRules?.length > 0),
				loadingMessage: this.props.implementedRules?.length == 0 ? 'No Rules Implemented' : 'Loading Existing Rules...'
			},
		];

		this.state.pageRows.forEach((rule, rowIndex) => {			
			tableRows.push(
				<Table.Row verticalAlign='middle' key={rowIndex} className={`em-c-table__row_${this.state.riskPorts[rowIndex]}`}>
					<>
						{this.props.rules.indexOf(rule) + 1}
					</>
					{this.state.columns?.filter(column => column.header !== "Protocol" && column.header !== "Description").map((column, colIndex) => (
						<React.Fragment key={`${rowIndex}-${colIndex}`}>
							{column.header === 'Operation' ? (<POROperationBadge>{this.getOperationName(rule)}</POROperationBadge>)
								: (column.header === 'Type' ? (<PORTypeBadge >{column.cell(rule)}</PORTypeBadge>)
									: (column.cell(rule)?.length > (column.limit || defaultLimit) ?
										(`${column.cell(rule).trim().substring(0, (column.limit || defaultLimit))}...`)
										:
										(column.cell(rule) || '-')))}
						</React.Fragment>
					))}
					<>
						{!this.props.readonly ?
							<ButtonGroup optionalClass="custom-Padding-Rule-Button">
								<Button
									style={{ zIndex: 0 }}
									variant="primary"
									size="small"
									onClick={() => this.setState({ ruleModalVisible: true, editingRule: rule })}
								>
									<Icon icon="pencil" size="small" />
								</Button>
								<Button
									style={{ zIndex: 0 }}
									variant="primary"
									size="small"
									onClick={() => this.handleDeleteOrRemoveRule(rule)}
								>
									<Icon icon="trashcan" size="small" />
								</Button>
							</ButtonGroup>
							:
							<Button
								style={{ zIndex: 0 }}
								variant="primary"
								size="small"
								onClick={() => this.setState({ ruleModalVisible: true, editingRule: rule })}
							>
								See details
							</Button>
						}
					</>
				</Table.Row>
			);
		});

		return (
			<>
				<p className="em-c-field__label">Ruleset</p>
				<Table
					header={
						<Toolbar>
							{this.props.readonly &&
								<ToolbarItem right>
									<CSVGeneratorButton
										data={this.props.rules}
										fileName={this.props.rules.length > 0 ? `POR-${this.props.rules[0].portOpeningRequestId}rules` : 'export.csv'}
										columns={this.state.columns}
									/>
								</ToolbarItem>}
							{!this.props.readonly &&
								<ToolbarItem right>
									<ButtonGroup>
										<Button
											variant="primary"
											size="small"
											disabled={!this.props.porType}
											onClick={() => this.setState({ ruleModalVisible: true })}
										>
											<Icon icon="plus-filled" size="small" />
											<span>Add New Rule</span>
										</Button>
										<Select
											placeholder='Import rules'
											value={this.state.optionValueSelected != null ? this.state.optionValueSelected : ''}
											onChange={(option) => this.handleOptionSelected(option)}
											components={{ Option: this.CustomOption }}
											options={options}
											styles={this.styles}
										/>
									</ButtonGroup>
								</ToolbarItem>}
						</Toolbar>
					}
					footer={
						<Paginator
							data={this.props.rules}
							onPageChange={this.handlePageChange}
						/>
					}
				>
					<Table.Head>
						<Table.Head.Row>
							<Table.Head.Cell />
							{tableHeaders}
							<Table.Head.Cell />
						</Table.Head.Row>
					</Table.Head>
					<Table.Body>
						{tableRows.length > 0 ?
							tableRows
							:
							<Table.Row>
								<Table.Cell
									colSpan={this.state.columns?.length + 2 || 2}
								>
									<i>No rules found.</i>
								</Table.Cell>
							</Table.Row>}
					</Table.Body>
				</Table>
				{this.state.showTrashcanModal &&
					<TrashcanModal
						type={this.props.porType}
						onClose={this.resetTrashcanModal}
						onDelete={this.handleDeleteRule}
						onRemove={this.handleRemoveRule}
					/>
				}
				{this.typeIs(OnPremPortOpeningRequestsTypes.InternetDMZ) &&
					<RuleModalInternetDMZ
						{...this.getRulesModalProps()}
						isReadOnly={this.props.readonly}
						direction={this.props.direction}
					/>}
				{this.typeIs(OnPremPortOpeningRequestsTypes.InternalCertConnection) &&
					<RuleModalCertifiedConnection
						{...this.getRulesModalProps()}
						isReadOnly={this.props.readonly}
					/>}
				{this.state.implementedRuleModalVisible &&
					<ImplementedRulesModal
						porType={this.props.porType}
						rules={this.props.implementedRules}
						onSuccess={this.handleImplementedRulesOnSuccess}
						visible={this.state.implementedRuleModalVisible}
						alreadySelected={this.props.selectedFromExisting}
						onCancel={() => {
							this.setState({
								implementedRuleModalVisible: false,
								optionValueSelected: '',
							});
						}}
					/>}
				<BulkUploadModal
					visible={this.state.bulkUploadModalVisible}
					type={this.props.porType}
					onSuccess={this.handleBulkUpload}
					onCancel={() => {
						this.setState({
							bulkUploadModalVisible: false,
							optionValueSelected: '',
						});
					}}
				/>
			</>
		);
	}
}